import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Table,
  Row,
  Col,
  Image,
  Tabs,
  Upload,
  message,
  Space,
  Empty,
  Popconfirm,
  Card,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  FileImageOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import SubPageLayout from '../helpers/SubPageLayout';
import {
  createProduct,
  createProductCategory,
  createProductDesign,
  createProductRange,
  deleteProduct,
  fetchProductMetadata,
  fetchProducts,
  getProductMetadata,
  getProducts,
  getProductsLoading,
  getProductsSaving,
  updateProduct,
} from '../../../features/products.store';

const PRODUCT_RANGES = {
  Chamber: ['MineSAFE', 'MineSAFE Perms', 'TunnelSAFE', 'ChemSAFE', 'CoalSAFE', 'StormSAFE', 'MediSAFE', 'Biora'],
  Ancillary: [],
  GuardIAN: [],
};

const PRODUCT_DESIGNS = {
  'MineSAFE': [
    { name: 'Standard Design', code: 'SD' },
    { name: 'Essential Design', code: 'ED' },
    { name: 'Narrow Design', code: 'ND' },
    { name: 'Compact Design', code: 'CD' },
    { name: 'Compact Design X-Series', code: 'CD-X' },
    { name: 'Standard Design Modular', code: 'SDM' },
  ],
  'TunnelSAFE': [],
  'ChemSAFE': [],
  'CoalSAFE': [],
  'StormSAFE': [],
  'MediSAFE': [],
  'Biora': [],
  'MineSAFE Perms': [],
};

const modalBodyHeight = 620;

// Beautiful summary card component
const SummaryCard = ({ icon: Icon, label, value, color = '#008540' }) => (
  <div
    style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #fbfdfc 100%)',
      border: `2px solid ${color}`,
      borderRadius: 12,
      padding: '10px 12px',
      marginBottom: 8,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 8px rgba(0, 133, 64, 0.08)',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 133, 64, 0.15)';
      e.currentTarget.style.transform = 'translateY(-2px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 133, 64, 0.08)';
      e.currentTarget.style.transform = 'translateY(0)';
    }}
  >
    <div
      style={{
        fontSize: 18,
        color: color,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <CheckCircleOutlined />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1.1 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginTop: 1, lineHeight: 1.2 }}>{value}</div>
    </div>
    <ArrowRightOutlined style={{ color: '#c7d2d9', fontSize: 14 }} />
  </div>
);

const Catalog = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState(null);
  const [filterRange, setFilterRange] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('category');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [partNumbers, setPartNumbers] = useState([]);
  const [tempPartNumber, setTempPartNumber] = useState({ pn: '', capacity: '' });
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFileList, setImageFileList] = useState([]);
  const [newRangeName, setNewRangeName] = useState('');
  const [newDesignName, setNewDesignName] = useState('');
  const [newDesignCode, setNewDesignCode] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isImagePreviewVisible, setIsImagePreviewVisible] = useState(false);

  const products = useSelector(getProducts);
  const productsLoading = useSelector(getProductsLoading);
  const productsSaving = useSelector(getProductsSaving);
  const productMetadata = useSelector(getProductMetadata);

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchProductMetadata());
  }, [dispatch]);

  const categoryOptions = productMetadata?.categories || [];

  // Get available ranges for selected category (including dynamic ones)
  const baseRanges = selectedCategory
    ? productMetadata?.rangesByCategory?.[selectedCategory] || PRODUCT_RANGES[selectedCategory] || []
    : [];
  const availableRanges = [...baseRanges];

  // Get available designs (including dynamic ones)
  const baseDesigns = selectedRange
    ? productMetadata?.designsByRange?.[selectedRange] || PRODUCT_DESIGNS[selectedRange] || []
    : [];
  const availableDesigns = [
    ...baseDesigns,
  ];

  const categoryFilterOptions = [...new Set(products.map((p) => p.category))];
  const rangeFilterOptions = [...new Set(
    products
      .filter((p) => !filterCategory || p.category === filterCategory)
      .map((p) => p.range)
  )];

  // Filter products by search, category and range
  const filteredProducts = products.filter((p) => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const searchMatches = !normalizedSearch
      || p.name.toLowerCase().includes(normalizedSearch)
      || p.category.toLowerCase().includes(normalizedSearch)
      || p.range.toLowerCase().includes(normalizedSearch);
    const categoryMatches = !filterCategory || p.category === filterCategory;
    const rangeMatches = !filterRange || p.range === filterRange;
    return searchMatches && categoryMatches && rangeMatches;
  });

  // Get selected product for detail
  const selectedProduct = products.find((p) => p.id === selectedProductId);
  const isDetailsOpen = Boolean(selectedProduct);
  const collapseDetailsPanel = () => setSelectedProductId(null);

  // Auto-advance tab when selection is made
  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setSelectedRange(null);
    setSelectedDesign(null);
    setTimeout(() => setActiveTab('range'), 300);
  };

  const handleRangeChange = (value) => {
    setSelectedRange(value);
    setSelectedDesign(null);
    setTimeout(() => setActiveTab('design'), 300);
  };

  const handleDesignChange = (value) => {
    setSelectedDesign(value);
    setTimeout(() => setActiveTab('partNumbers'), 300);
  };

  // ---- Modal handlers ----
  const openCreateModal = () => {
    setEditingProduct(null);
    setSelectedCategory(null);
    setSelectedRange(null);
    setSelectedDesign(null);
    setPartNumbers([]);
    setTempPartNumber({ pn: '', capacity: '' });
    setImagePreview(null);
    setImageFileList([]);
    form.resetFields();
    setActiveTab('category');
    setIsModalVisible(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setSelectedCategory(product.category);
    setSelectedRange(product.range);
    setSelectedDesign(product.design);
    setPartNumbers(product.partNumbers || []);
    setTempPartNumber({ pn: '', capacity: '' });
    setImagePreview(product.image || null);
    setImageFileList([]);
    form.setFieldsValue({
      name: product.name,
      description: product.description,
    });
    setActiveTab('category');
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingProduct(null);
  };

  // Part numbers management
  const addPartNumber = () => {
    if (!tempPartNumber.pn.trim() || !String(tempPartNumber.capacity || '').trim()) {
      message.error('Please enter both part number and capacity');
      return;
    }

    if (partNumbers.some((p) => p.pn === tempPartNumber.pn)) {
      message.error('This part number already exists');
      return;
    }

    setPartNumbers([
      ...partNumbers,
      {
        ...tempPartNumber,
        pn: tempPartNumber.pn.trim(),
        capacity: String(tempPartNumber.capacity).trim(),
      },
    ]);
    setTempPartNumber({ pn: '', capacity: '' });
    message.success('Part number added!');
  };

  const removePartNumber = (pn) => {
    setPartNumbers(partNumbers.filter((p) => p.pn !== pn));
  };

  const handleBeforeUpload = (file) => {
    if (!file.type.startsWith('image/')) {
      message.error('Please upload a valid image file.');
      return Upload.LIST_IGNORE;
    }

    setImagePreview(URL.createObjectURL(file));
    setImageFileList([file]);
    return false;
  };

  const handleRemoveImage = () => {
    setImageFileList([]);
    setImagePreview(null);
  };

  // Add new category inline
  const addNewCategory = () => {
    if (!newCategoryName.trim()) {
      message.error('Please enter a category name');
      return;
    }

    if (categoryOptions.includes(newCategoryName.trim())) {
      message.error('This category already exists');
      return;
    }

    dispatch(createProductCategory({
      name: newCategoryName.trim(),
    }))
      .unwrap()
      .then((result) => {
        const createdCategory = result?.name || newCategoryName.trim();
        setSelectedCategory(createdCategory);
        setSelectedRange(null);
        setSelectedDesign(null);
        setNewCategoryName('');
        message.success(`Category "${createdCategory}" added!`);
        dispatch(fetchProductMetadata());
        setTimeout(() => setActiveTab('range'), 500);
      })
      .catch((err) => {
        message.error(err?.message || 'Unable to add category');
      });
  };

  // Add new range inline
  const addNewRange = () => {
    if (!newRangeName.trim()) {
      message.error('Please enter a range name');
      return;
    }
    if (availableRanges.includes(newRangeName)) {
      message.error('This range already exists');
      return;
    }

    dispatch(createProductRange({
      category: selectedCategory,
      name: newRangeName.trim(),
    }))
      .unwrap()
      .then((result) => {
        const createdRange = result?.name || newRangeName.trim();
        setSelectedRange(createdRange);
        setNewRangeName('');
        message.success(`Range "${createdRange}" added!`);
        dispatch(fetchProductMetadata());
        setTimeout(() => setActiveTab('design'), 500);
      })
      .catch((err) => {
        message.error(err?.message || 'Unable to add range');
      });
  };

  // Add new design inline
  const addNewDesign = () => {
    if (!newDesignName.trim() || !newDesignCode.trim()) {
      message.error('Please enter both design name and code');
      return;
    }
    if (availableDesigns.some((d) => d.name === newDesignName)) {
      message.error('This design already exists');
      return;
    }
    dispatch(createProductDesign({
      range: selectedRange,
      name: newDesignName.trim(),
      code: newDesignCode.trim(),
    }))
      .unwrap()
      .then((result) => {
        const createdDesign = result?.name || newDesignName.trim();
        setSelectedDesign(createdDesign);
        setNewDesignName('');
        setNewDesignCode('');
        message.success(`Design "${createdDesign}" added!`);
        dispatch(fetchProductMetadata());
        setTimeout(() => setActiveTab('partNumbers'), 500);
      })
      .catch((err) => {
        message.error(err?.message || 'Unable to add design');
      });
  };

  // Product save handler
  const handleSaveProduct = () => {
    if (!selectedCategory || !selectedRange || !selectedDesign) {
      message.error('Please select Category, Range, and Design');
      return;
    }

    form
      .validateFields(['name', 'description'])
      .then((values) => {
        const imageFile = imageFileList[0]?.originFileObj || imageFileList[0] || null;

        const productPayload = {
          category: selectedCategory,
          range: selectedRange,
          design: selectedDesign,
          designCode: availableDesigns.find((d) => d.name === selectedDesign)?.code || '',
          name: values.name,
          description: values.description,
          partNumbers,
          imageUrl: imageFile ? null : (editingProduct?.image || null),
        };

        console.log('🔍 Product Update Debug:', {
          isEditing: Boolean(editingProduct),
          hasNewImage: Boolean(imageFile),
          oldImageUrl: editingProduct?.image,
          payloadImageUrl: productPayload.imageUrl,
          category: productPayload.category,
          range: productPayload.range,
          design: productPayload.design,
          name: productPayload.name,
        });

        if (editingProduct) {
          dispatch(updateProduct({
            id: editingProduct.id,
            payload: productPayload,
            image: imageFile,
          }))
            .unwrap()
            .then(() => {
              message.success('Product updated successfully');
              handleModalClose();
            })
            .catch((err) => {
              console.error('❌ Product update failed:', err);
              message.error(err?.message || 'Unable to update product');
            });
        } else {
          dispatch(createProduct({
            payload: productPayload,
            image: imageFile,
          }))
            .unwrap()
            .then(() => {
              message.success('Product created successfully');
              handleModalClose();
            })
            .catch((err) => {
              console.error('❌ Product create failed:', err);
              message.error(err?.message || 'Unable to create product');
            });
        }
      })
      .catch(() => {
        message.error('Please fill in all required fields');
      });
  };

  const handleDeleteProduct = (id) => {
    dispatch(deleteProduct({ id }))
      .unwrap()
      .then(() => {
        setSelectedProductId(null);
        message.success('Product deleted');
      })
      .catch((err) => {
        message.error(err?.message || 'Unable to delete product');
      });
  };

  // Table columns for product list
  const productTableColumns = [
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: 80,
      render: (image) => (
        <img
          src={image}
          alt="product"
          style={{
            width: 60,
            height: 60,
            objectFit: 'contain',
            borderRadius: 6,
            border: '1px solid #e5e7eb',
          }}
        />
      ),
    },
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
      width: 420,
      ellipsis: true,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 95,
    },
    {
      title: 'Range',
      dataIndex: 'range',
      key: 'range',
      width: 120,
    },
    {
      title: 'Design',
      dataIndex: 'design',
      key: 'design',
      width: 180,
      render: (design, record) => `${design} (${record.designCode})`,
    },
    {
      title: 'Part Numbers',
      dataIndex: 'partNumbers',
      key: 'partNumbers',
      width: 120,
      render: (pns) => (
        <span style={{ color: '#008540', fontWeight: 600 }}>
          {Array.isArray(pns) ? pns.length : 0}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          />
          <Popconfirm
            title="Delete Product"
            description="Are you sure you want to delete this product?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDeleteProduct(record.id)}
          >
            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <SubPageLayout
      title="Products"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          Create New Product
        </Button>
      }
    >
      <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 220px)' }}>
        {/* Left Panel: Product List */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Input
              allowClear
              size="large"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Search Products by name or category"
              style={{ maxWidth: 360 }}
            />

            <Select
              allowClear
              size="large"
              value={filterCategory}
              placeholder="Filter by Category"
              onChange={(value) => {
                setFilterCategory(value || null);
                setFilterRange(null);
              }}
              options={categoryFilterOptions.map((category) => ({
                label: category,
                value: category,
              }))}
              style={{ width: 190 }}
            />

            <Select
              allowClear
              size="large"
              value={filterRange}
              placeholder="Filter by Range"
              onChange={(value) => setFilterRange(value || null)}
              options={rangeFilterOptions.map((range) => ({
                label: range,
                value: range,
              }))}
              style={{ width: 180 }}
            />
          </div>

          <div style={{ flex: 1, overflowY: 'auto', borderRadius: 8 }}>
            <Table
              columns={productTableColumns}
              dataSource={filteredProducts}
              loading={productsLoading}
              rowKey="id"
              pagination={{ pageSize: 20 }}
              scroll={{ x: 1020 }}
              tableLayout="fixed"
              onRow={(record) => ({
                onClick: () => {
                  setSelectedProductId((prev) => (prev === record.id ? null : record.id));
                },
                style: {
                  cursor: 'pointer',
                  background: selectedProductId === record.id ? '#f0f9ff' : '',
                  borderLeft:
                    selectedProductId === record.id ? '3px solid #008540' : '3px solid transparent',
                },
              })}
              size="small"
            />
          </div>
        </div>

        {/* Right Panel: Product Detail */}
        <div
          style={{
            width: isDetailsOpen ? 430 : 0,
            minWidth: isDetailsOpen ? 430 : 0,
            opacity: isDetailsOpen ? 1 : 0,
            transform: isDetailsOpen ? 'translateX(0)' : 'translateX(24px)',
            transition: 'width 0.55s ease, min-width 0.55s ease, opacity 0.4s ease, transform 0.55s ease',
            overflow: 'hidden',
            pointerEvents: isDetailsOpen ? 'auto' : 'none',
          }}
        >
          {selectedProduct && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                minHeight: 0,
                height: '100%',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                padding: 20,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>
                  Product Details
                </h3>
                <Button
                  type="text"
                  shape="circle"
                  size="small"
                  title="Collapse details"
                  aria-label="Collapse product details"
                  icon={<ArrowRightOutlined />}
                  onClick={collapseDetailsPanel}
                  style={{
                    color: '#64748b',
                    border: '1px solid #dbe4ea',
                    background: '#f8fafc',
                    transition: 'all 0.25s ease',
                  }}
                />
              </div>

              <div
                style={{
                  height: 280,
                  background: '#ffffff',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  onClick={() => setIsImagePreviewVisible(true)}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    cursor: 'zoom-in',
                  }}
                />
                <Image
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  style={{ display: 'none' }}
                  preview={{
                    visible: isImagePreviewVisible,
                    onVisibleChange: (visible) => setIsImagePreviewVisible(visible),
                    src: selectedProduct.image,
                    scaleStep: 0.5,
                  }}
                />
              </div>

              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 6 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                    Product Name
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginTop: 4 }}>
                    {selectedProduct.name}
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                    Category
                  </div>
                  <div style={{ fontSize: 14, color: '#2d3748', marginTop: 4 }}>
                    {selectedProduct.category}
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                    Range
                  </div>
                  <div style={{ fontSize: 14, color: '#2d3748', marginTop: 4 }}>
                    {selectedProduct.range}
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                    Design
                  </div>
                  <div style={{ fontSize: 14, color: '#2d3748', marginTop: 4 }}>
                    {selectedProduct.design} ({selectedProduct.designCode})
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                    Description
                  </div>
                  <div style={{ fontSize: 13, color: '#4b5563', marginTop: 4, lineHeight: 1.5 }}>
                    {selectedProduct.description}
                  </div>
                </div>

                {selectedProduct.partNumbers && selectedProduct.partNumbers.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                      Part Numbers ({selectedProduct.partNumbers.length})
                    </div>
                    <div style={{ marginTop: 8 }}>
                      {selectedProduct.partNumbers.map((pn, idx) => (
                        <div
                          key={idx}
                          style={{
                            fontSize: 12,
                            padding: 6,
                            background: '#f9fafb',
                            borderRadius: 6,
                            marginBottom: 4,
                            fontFamily: 'monospace',
                          }}
                        >
                          <div style={{ fontWeight: 600, color: '#111827' }}>{pn.pn}</div>
                          <div style={{ color: '#6b7280' }}>Capacity: {pn.capacity}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Space style={{ width: '100%', marginTop: 16 }}>
                <Button style={{ flex: 1 }} onClick={() => openEditModal(selectedProduct)}>
                  Edit
                </Button>
                <Popconfirm
                  title="Delete"
                  description="Are you sure?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => handleDeleteProduct(selectedProduct.id)}
                >
                  <Button danger style={{ flex: 1 }}>
                    Delete
                  </Button>
                </Popconfirm>
              </Space>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Product Modal */}
      <Modal
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={980}
        centered
        destroyOnClose
        bodyStyle={{ padding: 0, height: modalBodyHeight, overflow: 'hidden' }}
        title={
          <div style={{ paddingRight: 20 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
              {editingProduct ? 'Edit Product' : 'Create New Product'}
            </div>
            {/* <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              Manage hierarchy and product details in one workflow.
            </div> */}
          </div>
        }
      >
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(180deg, #ffffff 0%, #fbfdfc 100%)',
          }}
        >
          <div style={{ flex: 1, minHeight: 0, padding: '0 18px' }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              tabBarGutter={32}
              size="large"
              tabBarStyle={{
                marginBottom: 12,
                borderBottom: '1px solid #e5e7eb',
                padding: '12px 14px 0',
              }}
            >
              {/* TAB 1: Category */}
              <Tabs.TabPane tab="Category" key="category">
                <div style={{ paddingTop: 12, maxHeight: modalBodyHeight - 190, overflowY: 'auto', paddingRight: 6 }}>
                  <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                      Select Product Category *
                    </label>
                    <Select
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      placeholder="Choose a category"
                      options={categoryOptions.map((cat) => ({
                        label: cat,
                        value: cat,
                      }))}
                      style={{ width: '100%' }}
                      size="large"
                    />
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                      Add a New Category
                    </label>
                    <Space style={{ width: '100%' }}>
                      <Input
                        placeholder="New category name"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <Button type="primary" onClick={addNewCategory} icon={<PlusOutlined />}>
                        Add
                      </Button>
                    </Space>
                  </div>

                  {selectedCategory && <SummaryCard label="Selected Category" value={selectedCategory} />}
                </div>
              </Tabs.TabPane>

              {/* TAB 2: Range */}
              <Tabs.TabPane tab="Range" key="range">
                <div style={{ paddingTop: 12, maxHeight: modalBodyHeight - 190, overflowY: 'auto', paddingRight: 6 }}>
                  <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                      Select Product Range *
                    </label>
                    {availableRanges.length > 0 ? (
                      <Select
                        value={selectedRange}
                        onChange={handleRangeChange}
                        placeholder="Choose a range"
                        options={availableRanges.map((range) => ({
                          label: range,
                          value: range,
                        }))}
                        style={{ width: '100%' }}
                        size="large"
                      />
                    ) : (
                      <div style={{ color: '#6b7280', fontStyle: 'italic', marginBottom: 16 }}>
                        {selectedCategory
                          ? `No ranges available for ${selectedCategory}.`
                          : 'Please select a category first.'}
                      </div>
                    )}
                  </div>

                  {selectedCategory && (
                    <div style={{ marginBottom: 24 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                        Add a New Range
                      </label>
                      <Space style={{ width: '100%' }}>
                        <Input
                          placeholder="New range name (e.g., TunnelSAFE)"
                          value={newRangeName}
                          onChange={(e) => setNewRangeName(e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <Button type="primary" onClick={addNewRange} icon={<PlusOutlined />}>
                          Add
                        </Button>
                      </Space>
                    </div>
                  )}

                  {selectedCategory && <SummaryCard label="Selected Category" value={selectedCategory} />}
                  {selectedRange && <SummaryCard label="Selected Range" value={selectedRange} />}
                </div>
              </Tabs.TabPane>

              {/* TAB 3: Design */}
              <Tabs.TabPane tab="Design" key="design">
                <div style={{ paddingTop: 12, maxHeight: modalBodyHeight - 190, overflowY: 'auto', paddingRight: 6 }}>
                  <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                      Select Design *
                    </label>
                    {availableDesigns.length > 0 ? (
                      <Select
                        value={selectedDesign}
                        onChange={handleDesignChange}
                        placeholder="Choose a design"
                        options={availableDesigns.map((design) => ({
                          label: `${design.name} (${design.code})`,
                          value: design.name,
                        }))}
                        style={{ width: '100%' }}
                        size="large"
                      />
                    ) : (
                      <div style={{ color: '#6b7280', fontStyle: 'italic', marginBottom: 16 }}>
                        {selectedRange
                          ? `No designs available for ${selectedRange}.`
                          : 'Please select a range first.'}
                      </div>
                    )}
                  </div>

                  {selectedRange && (
                    <div style={{ marginBottom: 24 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                        Add a New Design
                      </label>
                      <Row gutter={8}>
                        <Col span={16}>
                          <Input
                            placeholder="Design name (e.g., Custom Design)"
                            value={newDesignName}
                            onChange={(e) => setNewDesignName(e.target.value)}
                          />
                        </Col>
                        <Col span={8}>
                          <Input
                            placeholder="Code (e.g., CD)"
                            value={newDesignCode}
                            onChange={(e) => setNewDesignCode(e.target.value)}
                            maxLength={5}
                          />
                        </Col>
                      </Row>
                      <Button type="primary" onClick={addNewDesign} icon={<PlusOutlined />} style={{ marginTop: 8, width: '100%' }}>
                        Add Design
                      </Button>
                    </div>
                  )}

                  {selectedCategory && <SummaryCard label="Selected Category" value={selectedCategory} />}
                  {selectedRange && <SummaryCard label="Selected Range" value={selectedRange} />}
                  {selectedDesign && <SummaryCard label="Selected Design" value={selectedDesign} />}
                </div>
              </Tabs.TabPane>

              {/* TAB 4: Part Numbers */}
              <Tabs.TabPane tab="Part Numbers" key="partNumbers">
                <div style={{ paddingTop: 12, maxHeight: modalBodyHeight - 190, overflowY: 'auto', paddingRight: 6 }}>
                  <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', marginBottom: 12, fontWeight: 600 }}>
                      Add Part Numbers
                    </label>
                    <Form layout="vertical">
                      <Row gutter={12}>
                        <Col xs={16}>
                          <Form.Item label="Part Number" style={{ marginBottom: 8 }}>
                            <Input
                              placeholder="e.g. MS-SD1-08-SIV-36"
                              value={tempPartNumber.pn}
                              onChange={(e) =>
                                setTempPartNumber({ ...tempPartNumber, pn: e.target.value })
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={8}>
                          <Form.Item label="Capacity" style={{ marginBottom: 8 }}>
                            <Input
                              placeholder="e.g. 8 or Medium"
                              value={tempPartNumber.capacity}
                              onChange={(e) =>
                                setTempPartNumber({ ...tempPartNumber, capacity: e.target.value })
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Button
                        type="dashed"
                        block
                        onClick={addPartNumber}
                        style={{ marginBottom: 16 }}
                      >
                        + Add Part Number
                      </Button>
                    </Form>

                    {partNumbers.length > 0 && (
                      <div>
                        <label style={{ display: 'block', marginBottom: 12, fontWeight: 600 }}>
                          Added Part Numbers ({partNumbers.length})
                        </label>
                        <div style={{ maxHeight: 300, overflowY: 'auto', paddingRight: 2 }}>
                          {partNumbers.map((pn, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 10,
                                background: '#f9fafb',
                                borderRadius: 6,
                                marginBottom: 8,
                                border: '1px solid #e5e7eb',
                              }}
                            >
                              <div>
                                <div style={{ fontFamily: 'monospace', fontWeight: 600, color: '#111827' }}>
                                  {pn.pn}
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>
                                  Capacity: {pn.capacity}
                                </div>
                              </div>
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={() => removePartNumber(pn.pn)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedCategory && <SummaryCard label="Selected Category" value={selectedCategory} />}
                  {selectedRange && <SummaryCard label="Selected Range" value={selectedRange} />}
                  {selectedDesign && <SummaryCard label="Selected Design" value={selectedDesign} />}
                  {partNumbers.length > 0 && <SummaryCard label="Part Numbers Added" value={partNumbers.length} />}
                </div>
              </Tabs.TabPane>

              {/* TAB 5: Product Info */}
              <Tabs.TabPane tab="Product Info" key="info">
                <div style={{ paddingTop: 12, maxHeight: modalBodyHeight - 190, overflowY: 'auto', paddingRight: 6 }}>
                  <Form form={form} layout="vertical">
                    <Form.Item
                      label="Product Name"
                      name="name"
                      rules={[{ required: true, message: 'Please enter product name' }]}
                    >
                      <Input placeholder="e.g. MineSAFE Standard Design" />
                    </Form.Item>

                    <Form.Item
                      label="Description"
                      name="description"
                      rules={[{ required: true, message: 'Please enter description' }]}
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Brief description of the product..."
                      />
                    </Form.Item>

                    <Form.Item label="Product Image">
                      <div
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 10,
                          padding: 12,
                          background: '#fbfdfc',
                        }}
                      >
                        <Row gutter={12} align="middle">
                          <Col xs={14}>
                            <Upload.Dragger
                              accept="image/*"
                              multiple={false}
                              beforeUpload={handleBeforeUpload}
                              onRemove={handleRemoveImage}
                              fileList={imageFileList}
                              style={{ background: '#ffffff' }}
                            >
                              <p className="ant-upload-drag-icon">
                                <FileImageOutlined style={{ color: '#008540' }} />
                              </p>
                              <p className="ant-upload-text">Click or drag image to upload</p>
                              <p className="ant-upload-hint">PNG/JPG/SVG supported</p>
                            </Upload.Dragger>
                          </Col>
                          <Col xs={10}>
                            <div
                              style={{
                                height: 160,
                                border: '1px dashed #c7d2d9',
                                borderRadius: 10,
                                background: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                              }}
                            >
                              {imagePreview ? (
                                <img
                                  src={imagePreview}
                                  alt="Product preview"
                                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                              ) : (
                                <span style={{ color: '#94a3b8', fontSize: 12 }}>Image preview</span>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Form.Item>
                  </Form>

                  {selectedCategory && <SummaryCard label="Selected Category" value={selectedCategory} />}
                  {selectedRange && <SummaryCard label="Selected Range" value={selectedRange} />}
                  {selectedDesign && <SummaryCard label="Selected Design" value={selectedDesign} />}
                </div>
              </Tabs.TabPane>
            </Tabs>
          </div>

          <div
            style={{
              padding: '14px 18px',
              borderTop: '1px solid #e5e7eb',
              background: '#ffffff',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <Button onClick={handleModalClose}>Cancel</Button>
            {activeTab === 'info' && (
              <Button type="primary" onClick={handleSaveProduct} loading={productsSaving}>
                {editingProduct ? 'Update Product' : 'Create Product'}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </SubPageLayout>
  );
};

export default Catalog;
